import { AnalyzedScenarioFragment, CustomTarget } from '@predium/client-graphql';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSession } from '@/components/session/useSession';
import { useQueryGetActiveCustomTarget } from '@/sections/Scenarios/Scenario/queries/useQueryGetActiveCustomTarget';
import { useQueryGetAnalyzedScenario } from '@/sections/Scenarios/Scenario/queries/useQueryGetAnalyzedScenario';
import { useQueryGetScenario } from '@/sections/Scenarios/Scenario/queries/useQueryGetScenario';
import { useQueryGetScenariosBasicDetails } from '@/sections/Scenarios/Scenario/queries/useQueryGetScenariosBasicDetails';
import { ScenarioOutput } from '@/sections/Scenarios/Scenario/types';

type ScenarioContextProps = {
  scenario: ScenarioOutput | undefined | null;
  isLoadingScenario: boolean;
  activeScenarioVersion: NonNullable<ScenarioOutput>['scenario_versions'][0] | undefined;
  isOnLatestCostVersion: boolean;
  disableScenarioUpdate: boolean;
  scenarioNames: (scenarioId?: number) => string[];
  analyzedScenario?: AnalyzedScenarioFragment;
  isLoadingAnalyzedScenario: boolean;
  activeCustomTarget: CustomTarget | undefined;
  isWholeOrgScenario: boolean;
  buildingCount: number;
  canEdit: boolean;
  isArchived: boolean;
};

const ScenarioContext = createContext<ScenarioContextProps | null>(null);

type Props = PropsWithChildren<{
  scenarioId?: string; // For testing purposes
}>;

export const ScenarioContextProvider = ({ children, scenarioId: scenarioIdProp }: Props) => {
  const { isAdmin } = useSession();
  const { scenarioId: scenarioIdFromRoute } = useParams();
  const scenarioId = scenarioIdProp ?? scenarioIdFromRoute;

  const { scenario, isLoadingScenario, activeScenarioVersion, isOnLatestCostVersion } = useQueryGetScenario(
    Number(scenarioId),
  );
  const disableScenarioUpdate = !isOnLatestCostVersion && !isAdmin;
  const portfolio = scenario?.portfolio;
  const isWholeOrgScenario = !isLoadingScenario && !portfolio;

  const { scenarioNames } = useQueryGetScenariosBasicDetails();

  const { analyzedScenario, isLoadingAnalyzedScenario } = useQueryGetAnalyzedScenario(Number(scenarioId));

  const { activeCustomTarget } = useQueryGetActiveCustomTarget();

  const buildingCount = analyzedScenario?.building_count ?? scenario?.building_count ?? 0;
  const canEdit = scenario?.can_edit ?? false;
  const isArchived = scenario?.is_archived ?? false;

  const contextValue = useMemo(
    () => ({
      scenario,
      isLoadingScenario,
      isOnLatestCostVersion,
      activeScenarioVersion,
      disableScenarioUpdate,
      scenarioNames,
      analyzedScenario,
      isLoadingAnalyzedScenario,
      activeCustomTarget,
      isWholeOrgScenario,
      buildingCount,
      canEdit,
      isArchived,
    }),
    [
      scenario,
      isLoadingScenario,
      isOnLatestCostVersion,
      activeScenarioVersion,
      disableScenarioUpdate,
      scenarioNames,
      analyzedScenario,
      isLoadingAnalyzedScenario,
      activeCustomTarget,
      isWholeOrgScenario,
      buildingCount,
      canEdit,
      isArchived,
    ],
  );

  return <ScenarioContext.Provider value={contextValue}>{children}</ScenarioContext.Provider>;
};

export const useScenarioContext = () => {
  const value = useContext(ScenarioContext);

  if (!value) {
    throw new Error('useScenarioContext must be used within a ScenarioContextProvider');
  }

  return value;
};
